import React, { useState, useEffect, forwardRef } from "react";
import { cn } from "@/utils";

interface PassportInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlurValidate?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Компонент ввода паспорта РФ.
 * Формат: XXXX XXXXXX (4 цифры серия, пробел, 6 цифр номер)
 */
const PassportInput = forwardRef<HTMLInputElement, PassportInputProps>(
  ({ value, onChange, onBlurValidate, onBlur, placeholder = "0000 000000", className, disabled = false }, ref) => {
    const [displayValue, setDisplayValue] = useState("");
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
      setDisplayValue(value || "");
    }, [value]);

    const formatPassport = (input: string, prevValue: string): string => {
      // Разрешаем только цифры и пробелы
      const cleaned = input.replace(/[^\d ]/g, "");

      // Если пользователь удаляет символы, не форматируем
      if (cleaned.length < prevValue.length) {
        return cleaned.slice(0, 11);
      }

      // Если пользователь ввёл пробел сам после 4 цифр, оставляем
      if (cleaned.length === 5 && cleaned[4] === " ") {
        return cleaned;
      }

      // Автоформатирование: добавляем пробел после 4 цифр
      const digits = cleaned.replace(/ /g, "");
      let formatted = "";

      for (let i = 0; i < digits.length && i < 10; i++) {
        if (i === 4) {
          formatted += " ";
        }
        formatted += digits[i];
      }

      return formatted;
    };

    const validatePassport = (passportStr: string): string | null => {
      if (!passportStr || passportStr.length < 11) {
        return null;
      }

      const digits = passportStr.replace(/ /g, "");
      if (digits.length !== 10) {
        return "Паспорт должен содержать 10 цифр";
      }

      if (!/^\d{10}$/.test(digits)) {
        return "Паспорт должен содержать только цифры";
      }

      return null;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target.value;
      const formatted = formatPassport(input, displayValue);

      setDisplayValue(formatted);
      onChange(formatted);

      if (formatted.length === 11) {
        const validationError = validatePassport(formatted);
        setError(validationError);
      } else {
        setError(null);
      }
    };

    const handleBlur = () => {
      if (displayValue && displayValue.length > 0 && displayValue.length < 11) {
        setError("Введите полные данные паспорта");
      } else if (displayValue.length === 11 && !error && onBlurValidate) {
        onBlurValidate(displayValue);
      }
      onBlur?.();
    };

    return (
      <div className="w-full">
        <input
          ref={ref}
          type="text"
          value={displayValue}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={11}
          className={cn(
            "block w-full px-3 py-2 border rounded-lg bg-surface-elevated text-fs-sm text-ink-1 placeholder:text-ink-4",
            "transition-colors focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/30",
            error
              ? "border-red-400 focus:border-red-500 focus:ring-red-500/30 bg-red-50/50 dark:border-red-500 dark:bg-red-900/10"
              : "border-line",
            className,
          )}
        />
        {error && <p className="mt-1 text-xs text-error-600 dark:text-error-400">{error}</p>}
      </div>
    );
  },
);

PassportInput.displayName = "PassportInput";

export default PassportInput;
